<template>
  <section id="pages-view-container" :class="classList">
    <section v-if="pages" id="pages-container" :class="classList">
      <!-- Prev page Button -->
      <button
        v-if="allowArrowButtons"
        :class="{ inactive: !allowPrevButton }"
        @click="handlePrevPage"
        class="page-button"
      >
        <img
          src="~@/assets/icons/ui/chevron-back-outline.svg"
          alt="prev button arrow"
        />
      </button>

      <article
        v-for="(page, index) in actualPages"
        :id="'page-' + index"
        :key="index + '-page'"
        :class="'template-' + templateName"
        class="project-page"
      >
        <!-- <p class="page-count">PAGE : {{index + actualPage}}</p> -->
        <component
          v-if="page"
          :is="getPageComponentName(page.type)"
          :page="page"
          :pageId="index + actualPage"
          :activePageItem="activePageItem"
          :onePageMode="onePageMode"
          :layoutOverride="page.layoutOverride"
          v-on="{
            changePage,
            openPreviewNewLockerProducts: emitOpenPreviewNewLockerProducts,
            openSearchModal: emitOpenSearchModal,
          }"
        ></component>
      </article>

      <slot name="hidding-modal"></slot>
      <!-- Next page button -->
      <button
        v-if="actualPagesAreEven && allowArrowButtons"
        @click="handleNextPage"
        :class="{ inactive: !allowNextButton }"
        class="page-button"
      >
        <img
          src="~@/assets/icons/ui/chevron-back-outline.svg"
          alt="next button arrow"
        />
      </button>
    </section>

    <!-- Pagination -->
    <ProjectPagination
      :numberOfPages="numberOfPages"
      @changePage="changePage"
    />

    <!-- Loader -->
    <transition name="fade">
      <div class="loader-wrapper" v-if="loading || threekitLoading">
        <Loader
          :message="threekitLoadingMessage"
          :withMessage="threekitLoading"
        />
      </div>
    </transition>

    <!-- Modal to search products (Snapsheet only for now) -->
    <transition name="fade">
      <SearchProductsModal
        v-if="isSearchModalOpen"
        :isModalOpen="isSearchModalOpen"
        :activePageItem="activePageItem"
        :order="activePageItem.order"
        @closeModal="emitCloseModal"
      />
    </transition>

    <DecorationHiddenPlayer v-if="threekitReady && projectReady" />
  </section>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from "vuex";

import BackCover from "@/components/Projects/Playbooks/BackCover/BackCover.vue";
import Cover from "@/components/Projects/Playbooks/Cover/Cover.vue";
import DecorationHiddenPlayer from "@/components/Projects/Showrooms/DecorationHiddenPlayer";
import Designs from "@/components/Projects/Playbooks/Designs/Designs.vue";
import Extras from "@/components/Projects/Playbooks/Extras/Extras.vue";
import Hero from "@/components/Projects/Playbooks/Hero/Hero.vue";
import Index from "@/components/Projects/Playbooks/Index/Index.vue";
import Outfit from "@/components/Projects/Playbooks/Outfit/Outfit.vue";
import FiveDays from "@/components/Projects/Playbooks/Products/FiveDays.vue";
import Stock from "@/components/Projects/Playbooks/Products/Stock.vue";
import Sublimated from "@/components/Projects/Playbooks/Products/Sublimated.vue";
import ProjectPagination from "@/components/Projects/Project/ProjectPagination.vue";
import SearchProductsModal from "@/components/Projects/Snapsheets/SearchProductsModal.vue";
import SnapsheetPage from "@/components/Projects/Snapsheets/SnapsheetPage.vue";

import Loader from "@/components/Utils/Loader.vue";
export default {
  name: "PagesContainer",

  components: {
    BackCover,
    Cover,
    DecorationHiddenPlayer,
    Designs,
    Extras,
    Hero,
    Index,
    Loader,
    Outfit,
    FiveDays,
    Sublimated,
    Stock,
    ProjectPagination,
    SearchProductsModal,
    Snapsheet: SnapsheetPage,
  },

  props: {
    isSearchModalOpen: {
      type: Boolean,
    },
    activePageItem: {
      type: Object,
    },
  },

  computed: {
    ...mapState(["auth", "loading", "displayingIndex"]),

    ...mapState("playbook", {
      // playbookPages: (state) => state.pages,
      template: (state) => state.template,
    }),

    ...mapState("snapsheet", {
      snapsheetPages: (state) => state.pages,
    }),

    ...mapState("project", ["actualPage", "loading"]),

    ...mapState("threekit", {
      threekitLoading: (state) => state.loading,
      threekitLoadingMessage: (state) => state.loadingMessage,
    }),

    ...mapGetters("project", [
      "isCreateMode",
      "isEditMode",
      "isPlaybook",
      "isMobile",
      "onePageMode",
      "projectColors",
      "projectId",
    ]),

    ...mapGetters("playbook", {
      playbookPages: "filteredPages",
      playbookProducts: "playbookProducts",
    }),

    pagesCountOffset() {
      return this.isPlaybook ? -2 : 0;
    },

    pages() {
      return this.isPlaybook ? this.playbookPages : this.snapsheetPages;
    },

    templateName() {
      return this.template?.toLowerCase();
    },

    dealerApiToken() {
      return this.auth.apiToken;
    },

    classList() {
      return {
        "template-snapsheet-container": this.isSnapsheet,
        "without-arrows": !this.allowArrowButtons,
        "one-page": this.onePageMode,
        "create-mode": this.isCreateMode,
      };
    },

    actualPages() {
      if (this.onePageMode) {
        return [this.pages[this.actualPage - 1]];
      } else {
        return this.pages.slice(this.actualPage - 1, this.actualPage + 1);
      }
    },

    actualPagesAreEven() {
      if (this.onePageMode) return true;
      return this.actualPages.length % 2 === 0;
    },

    paginatedPages() {
      if (this.onePageMode) {
        return this.pages.length;
      }

      return this.pages.length % 2 === 0
        ? this.pages.length / 2
        : (this.pages.length + 1) / 2;
    },

    numberOfPages() {
      return this.pages?.length;
    },

    allowArrowButtons() {
      if (this.onePageMode) {
        return this.numberOfPages > 1;
      }
      return this.numberOfPages > 2;
    },

    allowNextButton() {
      if (this.onePageMode) {
        return this.actualPage !== this.numberOfPages;
      }
      return (
        this.actualPage + 1 !== this.numberOfPages &&
        this.actualPage !== this.numberOfPages
      );
    },

    allowPrevButton() {
      return this.actualPage !== 1;
    },

    projectReady() {
      return this.projectId > 0;
    },
  },

  methods: {
    ...mapActions("project", [
      "setPage",
      "setDefaultUniformAndSpiritwearDecorationColors",
    ]),
    ...mapActions("threekit", ["initThreekitColors"]),
    ...mapMutations("project", ["SET_SCREEN_WIDTH"]),

    getPageComponentName(pageType) {
      if (pageType?.toLowerCase() == "five day") {
        return "Sublimated";
      } else {
        return pageType;
      }
    },

    handlePrevPage() {
      if (this.allowPrevButton) {
        this.setPage(
          this.onePageMode ? this.actualPage - 1 : this.actualPage - 2
        );
      }
    },

    handleNextPage() {
      if (this.allowNextButton) {
        this.setPage(
          this.onePageMode ? this.actualPage + 1 : this.actualPage + 2
        );
      }
    },

    changePage(number) {
      this.setPage(this.onePageMode ? Number(number) : Number(number) + 1);
    },

    onResize() {
      this.SET_SCREEN_WIDTH(window.innerWidth);
    },

    emitOpenSearchModal(pr) {
      this.$emit("openSearchModal", pr);
    },

    emitOpenPreviewNewLockerProducts() {
      this.$emit("openPreviewNewLockerProducts");
    },

    emitCloseModal() {
      this.$emit("closeSearchModal");
    },
  },

  async mounted() {
    await this.setDefaultUniformAndSpiritwearDecorationColors();

    // await this.initThreekitColors();

    this.threekitReady = true;

    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize);
    });
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
  },

  data() {
    return {
      threekitReady: false,
    };
  },
};
</script>

<style lang="scss">
#app {
  .page {
    height: 100%;
    background: white;
    > .page-number-count {
      width: 100%;
      position: absolute;
      bottom: 0;
      padding: 0.25em 1em;
      font-size: 1.5em;
      font-family: "ForzaBold";
      text-align: left;
      text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff,
        1px 1px 0 #fff;
      &.right-page-number {
        text-align: right;
      }
    }
    .playbook-page-content {
      // display: flex;
      // flex: 1;
      // height: 100%;
      // justify-content: space-between;
      .playbook-page-column {
        padding: 0.5em 1em;
        flex: 1;
        width: 50%;
        display: flex;
        flex-direction: column;
        justify-content: space-around;
      }
    }
  }

  #pages-view-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    column-gap: 0.2rem;
    position: relative;
    margin: auto;
    width: 100%;
    max-width: 1250px;
    height: 100vh;
    overflow: visible;
    background: white;

    &.one-page {
      grid-template-columns: 1fr;
      max-width: 890px;

      .project-page {
        box-shadow: 0px 0px 18px 0px rgba(40, 42, 48, 0.08);
        max-width: 890px;
      }

      &.create-mode {
        // z-index: 10;
        margin: 0 auto;
        @media screen and (max-width: 1650px) {
          margin: 0 5% !important;
        }
      }
    }

    &.without-arrows {
      /* grid-template-columns: repeat(2, 1fr); */
      width: 100%;
    }

    #pages-container {
      display: flex;
      height: 95%;
      width: 100%;

      .project-page {
        width: 100%;
      }
    }

    .project-page {
      height: 900px;
      width: 50%;
      max-width: 690px;
      background: white;
      border-radius: 6px;
      /* overflow: hidden; */
      box-shadow: 10px 9px 18px 0px rgba(40, 42, 48, 0.08);
      position: relative;
      overflow: visible;

      h2 {
        text-transform: uppercase;
      }
      &.template-fsg,
      &.template-ua {
        height: 100%;
      }
      .page-count {
        position: absolute;
        left: 0;
        top: 0;
      }
      &:nth-child(even) {
        box-shadow: -10px 9px 18px 0px rgba(40, 42, 48, 0.08);
      }
      .project-title-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem 2rem;
        border-bottom: 3px solid #666;
        .form-control {
          width: 50%;
          padding: 6px;
          border-radius: 3px;
          border: 1px solid #666;
        }
        .edit-action-buttons {
          width: 50%;
          display: flex;
          justify-content: flex-end;
          button {
            width: 100px;
            margin: 0 5px;
            display: block;
            padding: 0.4rem;
            background-color: white;
            border-radius: 7px;
            border: none;
            box-shadow: 0 0 10px -2px #0000004d;
            color: black;
            font-family: "SlateStd";
            font-weight: bold;
            text-transform: uppercase;
            transition: box-shadow 0.2s ease;
            transition: background-color 0.15s ease;
            transition: color 0.15s ease;
            &:hover {
              cursor: pointer;
              color: white;
              background-color: black;
            }
            &.save {
              color: white;
              background-color: black;
            }
          }
        }
        .project-title-edit-action {
          font-size: 14px;
          color: #666;
          text-decoration: underline;
          cursor: pointer;
        }
      }
    }

    button.page-button {
      height: 20%;
      width: 3rem;
      background: $dark;
      border: none;
      margin-top: auto;
      margin-bottom: auto;
      margin-left: 1rem;
      cursor: pointer;
      transition: all 0.2s;
      padding: 0 0.1rem;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: -2rem;
      z-index: 10;

      &:not(.inactive):hover {
        box-shadow: 0 0 10px 0px rgba(0, 0, 0, 0.541);
        transform: translateY(-50%) scale(1.1);
      }
      img {
        height: 36px;
        width: 36px;
      }

      &:nth-of-type(odd) {
        margin-left: 0;
        margin-right: 1rem;
        left: -2rem;
      }

      &:nth-of-type(even) {
        img {
          transform: rotate(180deg);
        }
      }
      &.inactive {
        background: $placeholder;
        cursor: initial;
        &:hover {
          transform: translateY(-50%);
        }
      }
    }

    .loader-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(40, 42, 48, 0.08);
      z-index: 10;
    }

    @media screen and (max-width: 1366px) {
      &.without-arrows {
        grid-template-columns: repeat(1, 1fr);
        width: 85%;
      }
      &.one-page {
        max-width: 520px;
        .project-page.one-page {
          max-width: 520px;
        }
      }
    }
    @media screen and (max-width: 1024px) {
      grid-template-columns: 1fr 10fr 1fr;
      width: 55%;
      &.without-arrows {
        grid-template-columns: repeat(1, 1fr);
        width: 65%;
      }
    }
    @media screen and (max-width: 768px) {
      grid-template-columns: 1fr 10fr 1fr;
      width: 80%;
      &.without-arrows {
        grid-template-columns: repeat(1, 1fr);
        width: 80%;
      }
    }

    @media screen and (max-width: 576px) {
      width: 95%;
      button.page-button {
        height: 100px;
        position: absolute;
        top: 50%;
        &:nth-of-type(even) {
          right: 0;
        }
        &:nth-of-type(odd) {
          left: 0;
        }
      }
    }

    @media screen and (max-width: 375px) {
      grid-template-columns: 25px 10fr 25px;

      button.page-button {
        margin-left: 0;

        &:nth-of-type(odd) {
          margin-right: 0;
        }

        img {
          height: 25px;
          width: 25px;
        }
      }
    }
  }
}
.template-snapsheet-container {
  display: flex;
}
</style>
